import { getOccToken, clearOccToken } from "./occToken";
import { triggerSessionExpired } from "~/app/api/triggerSessionExpired";
import { type ExplicitOccAccountContext, type OccRequestOptions } from "~/common/api/occTransport";
import { getSessionSelectedAccount } from "~/domains/user/sessionRuntime";

const OCC_PATH_PREFIX = "/occ/";

export type OccFetchOptions = OccRequestOptions;

function isOccUrl(url: string): boolean {
  try {
    const { pathname } = new URL(url, window.location.origin);
    return pathname.startsWith(OCC_PATH_PREFIX);
  } catch {
    return url.startsWith(OCC_PATH_PREFIX);
  }
}

function appendAccountContext(url: string, explicitAccount?: ExplicitOccAccountContext): string {
  const [path, queryString = ""] = url.split("?", 2);
  const params = new URLSearchParams(queryString);

  const lang = window.__SCHA_BOOT__?.language;
  if (lang && !params.has("lang")) {
    params.set("lang", lang);
  }

  const runtimeAccount = explicitAccount ? null : getSessionSelectedAccount();
  const account = explicitAccount
    ? {
        uid: explicitAccount.soldTo,
        salesOrg: explicitAccount.salesOrg,
        endCustomerCode: explicitAccount.endCustomer,
      }
    : runtimeAccount;
  const hasExplicitAccountContext = params.has("soldTo") || params.has("salesOrg") || params.has("endCustomer");
  if (!hasExplicitAccountContext && account?.uid && account.salesOrg) {
    if (account.uid) {
      params.set("soldTo", account.uid);
    }
    if (account.salesOrg) {
      params.set("salesOrg", account.salesOrg);
    }
    if (account.endCustomerCode) {
      params.set("endCustomer", account.endCustomerCode);
    }
  }

  const query = params.toString();
  if (!query) {
    return path;
  }
  return `${path}?${query}`;
}

export function occUrl(path: string): string {
  return `${occBaseSiteUrl("")}/schaeffler${path}`;
}

export function occBaseSiteUrl(path: string): string {
  const siteUid = window.__SCHA_BOOT__?.siteUid;
  if (!siteUid) {
    throw new Error("Cannot build OCC URL without __SCHA_BOOT__.siteUid");
  }
  return `/occ/v2/${siteUid}${path}`;
}

async function occFetchWithRefresh(
  url: string,
  init: RequestInit,
  options: OccFetchOptions,
  _isRetry = false,
): Promise<Response> {
  const token = await getOccToken();
  const headers = new Headers(init.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const requestUrl =
    options.accountContext === "omit"
      ? url
      : appendAccountContext(url, typeof options.accountContext === "object" ? options.accountContext : undefined);
  const response = await fetch(requestUrl, {
    ...init,
    headers,
  });

  if (response.status === 401 && !_isRetry) {
    clearOccToken();
    return occFetchWithRefresh(url, init, options, true);
  }

  if (response.status === 401 && _isRetry) {
    triggerSessionExpired();
    const error = new Error(`OCC session expired: ${response.status}`);
    (error as Error & { status: number }).status = response.status;
    throw error;
  }

  return response;
}

export async function occFetch(url: string, init: RequestInit = {}, options: OccFetchOptions = {}): Promise<Response> {
  if (!isOccUrl(url)) {
    return fetch(url, init);
  }
  return occFetchWithRefresh(url, init, options);
}

/** Visible for testing. */
export { appendAccountContext, isOccUrl };
